import { el, clear } from '../../utils/dom.js';
import { Reservations } from '../../api/resources.js';
import { fmtDateTime } from '../../utils/format.js';
import { dataTable } from '../../components/table.js';
import { statusBadge } from '../../components/badge.js';
import { openModal, confirm } from '../../components/modal.js';
import { field, readForm } from '../../components/form.js';
import { ok, err } from '../../components/toast.js';
import { navigate } from '../../router/index.js';

export default async function ReservationsDetailPage(outlet, ctx) {
  clear(outlet);
  const id = ctx.params.id;
  let r;
  try { r = await Reservations.get(id); }
  catch (ex) { outlet.innerHTML = `<div class="empty">Error: ${ex.message}</div>`; return; }

  const canConfirm = r.status === 'pendiente';
  const canCancel  = ['pendiente','confirmada'].includes(r.status);
  const canConvert = canCancel;

  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {},
      el('h1', {}, r.code),
      el('div', { class: 'muted' }, statusBadge(r.status), ' · ', r.client_name || r.responsible_name),
    ),
    el('div', { class: 'flex gap-2 wrap' },
      el('a', { href: '#/reservations', class: 'btn ghost' }, '← Volver'),
      canConfirm ? el('button', { class: 'btn', onclick: confirmRes }, 'Confirmar') : null,
      canConvert && r.responsible_id ? el('button', { class: 'btn primary', onclick: () => convertLoan() }, 'Convertir a prestamo') : null,
      canConvert && r.client_id      ? el('button', { class: 'btn primary', onclick: () => convertRental() }, 'Convertir a renta') : null,
      canCancel ? el('button', { class: 'btn danger', onclick: cancelRes }, 'Cancelar') : null,
    ),
  ));

  outlet.appendChild(el('div', { class: 'card mb-4' },
    el('div', { class: 'card-head' }, el('h3', {}, 'Datos')),
    el('div', { style: 'display:grid;grid-template-columns:repeat(3,1fr);gap:var(--sp-3);' },
      kv('Cliente',      r.client_name),
      kv('Responsable',  r.responsible_name),
      kv('Inicio',       fmtDateTime(r.start_date)),
      kv('Fin',          fmtDateTime(r.end_date)),
      kv('Expira',       r.expires_at ? fmtDateTime(r.expires_at) : '—'),
      kv('Motivo',       r.reason || '—'),
    ),
  ));

  outlet.appendChild(el('div', { class: 'card' },
    el('div', { class: 'card-head' }, el('h3', {}, `Items (${(r.items||[]).length})`)),
    dataTable({
      columns: [
        { key: 'item_name',    label: 'Item' },
        { key: 'serial_number', label: 'Serial / Codigo', render: (x) => el('span', { class: 'text-mono text-sm' }, x.serial_number || x.unit_internal_code || (x.item_kind === 'bulk' ? 'BULK' : '—')) },
        { key: 'quantity',     label: 'Cantidad', className: 'right' },
      ],
      rows: r.items || [],
    }),
  ));

  function reload() { ReservationsDetailPage(outlet, ctx); }

  async function confirmRes() {
    try { await Reservations.confirm(id); ok('Reserva confirmada'); reload(); }
    catch (ex) { err(ex.message); }
  }
  async function cancelRes() {
    if (!await confirm({ title: 'Cancelar reserva', message: `Cancelar ${r.code}?`, danger: true, confirmText: 'Cancelar reserva' })) return;
    try { await Reservations.cancel(id); ok('Reserva cancelada'); reload(); }
    catch (ex) { err(ex.message); }
  }

  async function convertLoan() {
    if (!await confirm({ title: 'Convertir a prestamo', message: `Crear prestamo desde ${r.code}?` })) return;
    try {
      const loan = await Reservations.convertToLoan(id, {});
      ok(`Prestamo ${loan.code} creado`);
      navigate(`/loans/${loan.id}`);
    } catch (ex) { err(ex.message); }
  }

  function convertRental() {
    // Necesitamos pedir precio por linea
    const form = el('form', {
      onsubmit: async (e) => {
        e.preventDefault();
        const d = readForm(form);
        const prices = (r.items || []).map((it) => ({
          reservation_item_id: it.id,
          price_per_day: Number(d[`p_${it.id}`] || 0),
        }));
        try {
          const rental = await Reservations.convertToRental(id, {
            prices,
            discount: Number(d.discount || 0),
            tax: Number(d.tax || 0),
            deposit: Number(d.deposit || 0),
            notes: d.notes || undefined,
          });
          ok(`Renta ${rental.code} creada`);
          modal.close();
          navigate(`/rentals/${rental.id}`);
        } catch (ex) { err(ex.message); }
      },
    });

    form.appendChild(el('h3', {}, 'Precios por linea'));
    for (const it of r.items || []) {
      form.appendChild(field({
        name: `p_${it.id}`,
        label: `${it.item_name} ${it.serial_number ? '(' + it.serial_number + ')' : ''} × ${it.quantity}`,
        type: 'number', step: '0.01', required: true,
      }));
    }
    form.appendChild(el('div', { class: 'form-grid mt-3' },
      field({ name: 'discount', label: 'Descuento', type: 'number', step: '0.01', value: 0 }),
      field({ name: 'tax',      label: 'Impuesto',  type: 'number', step: '0.01', value: 0 }),
      field({ name: 'deposit',  label: 'Deposito',  type: 'number', step: '0.01', value: 0 }),
      field({ name: 'notes',    label: 'Notas', type: 'textarea', full: true }),
    ));
    const cancel = el('button', { class: 'btn', type: 'button', onclick: () => modal.close() }, 'Cancelar');
    const submit = el('button', { class: 'btn primary', type: 'submit' }, 'Crear renta');
    const modal = openModal({ title: 'Convertir a renta', body: form, footer: [cancel, submit], size: 'lg' });
  }
}

function kv(label, value) {
  return el('div', {},
    el('div', { class: 'text-xs muted' }, label),
    el('div', { class: 'text-bold' }, value || '—'),
  );
}
